import { useToast } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { createContext, useContext, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  BidV2Status,
  Currency,
  SourcingEventStatus,
  useAwardSourcingEventMutation,
  useGetEvaluationQuery,
  type EvaluationDto,
  type NegotiationRoundV2Dto,
  type SourcingEventV2Dto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useRoundSelection } from "../rounds/RoundSelectionContext";
import { basketBidIds } from "./basketBidIds";
import { canSelectWinner } from "./canSelectWinner";
import { hasStandingAwardDecision } from "./hasStandingAwardDecision";
import { seedWinnerIds } from "./seedWinnerIds";

interface AwardSelection {
  evaluation: EvaluationDto | undefined;
  isLoading: boolean;
  isError: boolean;
  winnerIds: string[];
  toggleWinner: (bidId: string) => void;
  internalNote: string;
  setInternalNote: (note: string) => void;
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  amount: string;
  setAmount: (amount: string) => void;
  award: () => Promise<void>;
  isAwarding: boolean;
}

const NOTHING_TO_AWARD: AwardSelection = {
  evaluation: undefined,
  isLoading: false,
  isError: false,
  winnerIds: [],
  toggleWinner: () => undefined,
  internalNote: "",
  setInternalNote: () => undefined,
  currency: Currency.Nok,
  setCurrency: () => undefined,
  amount: "",
  setAmount: () => undefined,
  award: () => Promise.resolve(),
  isAwarding: false,
};

const AwardSelectionContext = createContext<AwardSelection>(NOTHING_TO_AWARD);

interface AwardForm {
  eventId: string;
  /** null while the seeded winners still stand; an array once the buyer has touched the ticks. */
  picked: string[] | null;
  internalNote: string;
  currency: Currency;
  amount: string;
}

const emptyForm = (event: SourcingEventV2Dto): AwardForm => {
  const estimated = event.eventContext.type === "Public" ? event.eventContext.estimatedValue : null;
  return {
    eventId: event.id,
    picked: null,
    internalNote: "",
    currency: estimated?.currency ?? Currency.Nok,
    amount: estimated?.value != null ? String(estimated.value) : "",
  };
};

const collectsAward = (
  event: SourcingEventV2Dto,
  rounds: readonly NegotiationRoundV2Dto[],
): boolean =>
  (event.status === SourcingEventStatus.Evaluation || event.status === SourcingEventStatus.Evaluated) &&
  canSelectWinner(event, rounds) &&
  !hasStandingAwardDecision(event);

export const AwardSelectionProvider = ({
  event,
  children,
}: {
  event: SourcingEventV2Dto;
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { trigger } = useApiError();
  const { rounds, latestRound } = useRoundSelection();
  const {
    data: evaluation,
    isLoading,
    isError,
  } = useGetEvaluationQuery(
    collectsAward(event, rounds) ? { eventId: event.id, round: latestRound?.id } : skipToken,
  );
  const [awardEvent, { isLoading: isAwarding }] = useAwardSourcingEventMutation();

  // React Router reuses SourcingEventView across a :eventId change, so the form has to notice.
  const [form, setForm] = useState(() => emptyForm(event));
  if (form.eventId !== event.id) setForm(emptyForm(event));
  const { picked, internalNote, currency, amount } = form;

  const awardable = new Set(
    (evaluation?.bids ?? [])
      .filter((bid) => bid.status !== BidV2Status.Rejected)
      .map((bid) => bid.bidId),
  );
  const seeded = evaluation ? seedWinnerIds(evaluation, basketBidIds(event, latestRound)) : [];
  const winnerIds = (picked ?? seeded).filter((id) => awardable.has(id));

  const change = (fields: Partial<AwardForm>) => setForm((previous) => ({ ...previous, ...fields }));

  const toggleWinner = (bidId: string) =>
    change({
      picked: winnerIds.includes(bidId)
        ? winnerIds.filter((id) => id !== bidId)
        : [...winnerIds, bidId],
    });

  const award = async () => {
    if (winnerIds.length === 0) return;
    const parsed = amount.trim() === "" ? null : Number(amount);
    try {
      await awardEvent({
        eventId: event.id,
        awardSourcingEventRequest: {
          winningBidIds: winnerIds,
          internalNote: internalNote.trim() || null,
          eventValue: parsed != null && !Number.isNaN(parsed) ? { value: parsed, currency } : null,
        },
      }).unwrap();
      toast({ title: t("Event awarded"), status: "success", position: "top-right" });
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <AwardSelectionContext.Provider
      value={{
        evaluation,
        isLoading,
        isError,
        winnerIds,
        toggleWinner,
        internalNote,
        setInternalNote: (note) => change({ internalNote: note }),
        currency,
        setCurrency: (next) => change({ currency: next }),
        amount,
        setAmount: (next) => change({ amount: next }),
        award,
        isAwarding,
      }}
    >
      {children}
    </AwardSelectionContext.Provider>
  );
};

export const useAwardSelection = (): AwardSelection => useContext(AwardSelectionContext);
